import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import Input from "../input/Input";
import Message from "../message/Message";
import Spinner from "../spinner/Spinner";
import {
  CreateProductModalProps,
  AddOnProps,
} from "../../types/ComponentProps";
import ToggleSwitch from "../toggleSwitch/ToggleSwitch";
import { categoryOptions } from "./dropdown";
import CustomSelectAddOns from "../customSelectAddOns/CustomSelectAddOns";
import useGetProductById from "../../hooks/useGetProductById";
import CustomSelectProduct from "../customSelectProduct/CustomSelectProduct";
import ProductListStyles from "./ProductListStyles.module.css";
import useGetAllAddOns from "../../hooks/useGetAllAddOns";
import MerchVariant from "./MerchVariant";

const UpdateProductModal = ({
  onCloseClickHandler,
  inputChangeHandler,
  createProductForm,
  createProductIsLoading,
  errorHandler,
  setCreateProductForm,
  activeProductId,
  toggleState,
  updateProductionSubmitHandler,
  setToggleState,
  productId,
  productType,
}: CreateProductModalProps) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { getAllAddOnsData } = useGetAllAddOns();
  const { isLoadingGetProductById, dataGetProductById } = useGetProductById(
    Number(productId),
    String(productType)
  );
  const handleImageLoad = () => setImageLoaded(true);
  const handleImageError = () => setImageLoaded(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  // Handle Escape key to close modal
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape" && !createProductIsLoading) {
        setPreview(null);
        onCloseClickHandler();
      }
    },
    [onCloseClickHandler, createProductIsLoading]
  );

  // Focus trap within modal
  const handleFocusTrap = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab") return;

    const focusableSelectors = [
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex="0"]',
      'a[href]'
    ].join(', ');

    const focusableElements = modalRef.current?.querySelectorAll<HTMLElement>(focusableSelectors);
    
    if (!focusableElements || focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keydown", handleFocusTrap);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keydown", handleFocusTrap);
    };
  }, [handleKeyDown, handleFocusTrap]);

  // Focus close button only on initial mount
  useEffect(() => {
    closeButtonRef.current?.focus();
  }, []); // Empty dependency array = only on mount

  useEffect(() => {
    if (dataGetProductById?.product) {
      setCreateProductForm((prevForm: any) => ({
        ...prevForm,
        productName: dataGetProductById.product.productName || "",
        type: dataGetProductById.product.type || "",
        price: dataGetProductById.product.price || "",
        quantity: dataGetProductById.product.quantity || "",
        category: dataGetProductById.product.category || "",
        productDescription: dataGetProductById.product.productDescription || "",
        addOns: [...(dataGetProductById?.product?.addOns ?? [])],
        variant:
          Array.isArray(dataGetProductById.product.variant) &&
          dataGetProductById.product.variant.length
            ? dataGetProductById.product.variant
            : null,
      }));

      setToggleState(!!dataGetProductById.product.addOns?.length);
      setPreview(dataGetProductById.product.imageURL || null);
    }
  }, [
    dataGetProductById,
    setCreateProductForm,
    activeProductId,
    setToggleState,
  ]);

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "image/jpeg": [], "image/png": [] },
    onDrop: (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        setPreview(URL.createObjectURL(file));
        inputChangeHandler({ target: { name: "productImage", value: file } });
      }
    },
  });

  const filterDataAddOns = useMemo(() => {
    return (
      getAllAddOnsData?.pages?.flatMap((page) =>
        page?.addons.filter(
          (addOn: AddOnProps) =>
            !createProductForm?.addOns?.some(
              (selectedAddOn) =>
                Number(selectedAddOn.addonId) === Number(addOn.addonId)
            )
        )
      ) ?? []
    );
  }, [getAllAddOnsData, createProductForm?.addOns]);

  const handleSelectAddOns = (selectedOption: AddOnProps) => {
    setCreateProductForm((prevForm: any) => {
      const addOnsArray = Array.isArray(prevForm.addOns) ? prevForm.addOns : [];
      const isDuplicate = addOnsArray.some(
        (addOn: AddOnProps) => addOn.addonId === selectedOption.addonId
      );
      if (isDuplicate) return prevForm;
      const updatedForm = {
        ...prevForm,
        addOns: [...addOnsArray, selectedOption],
      };
      return updatedForm;
    });
  };

  const handleSelectTypeAndCategory =
    (field: "type" | "category") =>
    (selectedOption: { value: string; label: string }) => {
      setCreateProductForm((prevForm: any) => ({
        ...prevForm,
        [field]: selectedOption.value,
      }));
    };

  const handleClose = () => {
    setPreview(null);
    onCloseClickHandler();
  };

  const removeListAddOns = (addonId?: number) => {
    setCreateProductForm((prevForm: any) => ({
      ...prevForm,
      addOns: addonId
        ? prevForm.addOns.filter(
            (addOn: AddOnProps) => Number(addOn.addonId) !== Number(addonId)
          )
        : null,
    }));
  };

  return (
    <div 
      className={ProductListStyles.createProductModal}
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-product-modal-title"
    >
      <div 
        className={ProductListStyles.createProductModalMainContent}
        ref={modalRef}
      >
        <div className={ProductListStyles.flexBoxBlk}>
          <div>
            <h1 id="update-product-modal-title">Edit Product</h1>
          </div>
          <div>
            <button
              ref={closeButtonRef}
              className={`fa-solid fa-xmark ${ProductListStyles.closeIconCreateBadge}`}
              onClick={handleClose}
              disabled={createProductIsLoading}
              aria-label="Close modal"
            ></button>
          </div>
        </div>

        <hr className={ProductListStyles.horizontalLine} />

        {isLoadingGetProductById ? (
          <div style={{ marginTop: "20px" }}>
            <Spinner variant="medium-spinner" alignSpin="center-spinner" />
          </div>
        ) : (
          <div className={ProductListStyles.scrollContainer}>
            {createProductForm?.type === "Voucher" && (
              <div>
                <div className={ProductListStyles.formSection}>
                  <h2 style={{ marginTop: "20px" }} id="update-dropzone-label">Image</h2>
                  <div
                    {...getRootProps()}
                    className={ProductListStyles.dropzone}
                    role="button"
                    aria-labelledby="update-dropzone-label"
                    aria-describedby="update-dropzone-instructions"
                  >
                    <input {...getInputProps()} aria-label="Upload product image" />
                    {preview ? (
                      <div className={ProductListStyles.previewImageBlk}>
                        {!imageLoaded && (
                          <div className={ProductListStyles.spinnerBlkDiv}>
                            <Spinner
                              variant="medium-spinner"
                              alignSpin="center-spinner"
                            />
                          </div>
                        )}
                        <img
                          src={preview}
                          alt="Product image preview"
                          className={ProductListStyles.imagePreview}
                          onLoad={handleImageLoad}
                          onError={handleImageError}
                          style={{ display: imageLoaded ? "block" : "none" }}
                        />
                      </div>
                    ) : (
                      <img
                        src={dataGetProductById?.product?.imageURL}
                        alt="Current product image"
                        className={ProductListStyles.imagePreview}
                      />
                    )}
                    <p id="update-dropzone-instructions" className={ProductListStyles.visuallyHidden}>
                      Click or drag to upload a new image. Accepts JPEG and PNG.
                    </p>
                  </div>
                  <h2>Basic Details</h2>

                  <div className={ProductListStyles.inputFlexBox}>
                    <div>
                      <Input
                        type="text"
                        label="Product Name"
                        onChangeHandler={inputChangeHandler}
                        name="productName"
                        value={createProductForm?.productName}
                      />
                    </div>
                    <div>
                      <Input
                        type="number"
                        label="Price"
                        onChangeHandler={inputChangeHandler}
                        name="price"
                        maxLength={6}
                        value={
                          createProductForm?.price
                            ? createProductForm.price.toString()
                            : ""
                        }
                      />
                    </div>
                  </div>

                  <div className={ProductListStyles.inputFlexBox}>
                    <div>
                      <Input
                        type="number"
                        label="Quantity"
                        maxLength={6}
                        onChangeHandler={inputChangeHandler}
                        name="quantity"
                        value={
                          createProductForm?.quantity !== null
                            ? createProductForm.quantity.toString()
                            : ""
                        }
                      />
                    </div>
                    <div>
                      <div className={ProductListStyles.dropDownFlexContent}>
                        <label id="update-voucher-category-label">Category</label>
                        <CustomSelectProduct
                          options={categoryOptions}
                          onSelect={handleSelectTypeAndCategory("category")}
                          placeholder="Select category"
                          value={
                            categoryOptions.find(
                              (option) =>
                                option.value === createProductForm?.category
                            ) || null
                          }
                          id="update-voucher-category-select"
                        />
                      </div>
                    </div>
                  </div>

                  <Input
                    type="text"
                    label="Description"
                    onChangeHandler={inputChangeHandler}
                    name="productDescription"
                    value={createProductForm?.productDescription || ""}
                  />
                </div>
              </div>
            )}

            {createProductForm?.type === "Merch" && (
              <div className={ProductListStyles.formSection}>
                <h2 style={{ marginTop: "20px" }} id="update-merch-dropzone-label">Image</h2>
                <div 
                  {...getRootProps()} 
                  className={ProductListStyles.dropzone}
                  role="button"
                  aria-labelledby="update-merch-dropzone-label"
                  aria-describedby="update-merch-dropzone-instructions"
                >
                  <input {...getInputProps()} aria-label="Upload product image" />
                  {preview ? (
                    <div className={ProductListStyles.previewImageBlk}>
                      {!imageLoaded && (
                        <div className={ProductListStyles.spinnerBlkDiv}>
                          <Spinner
                            variant="medium-spinner"
                            alignSpin="center-spinner"
                          />
                        </div>
                      )}
                      <img
                        src={preview}
                        alt="Product image preview"
                        className={ProductListStyles.imagePreview}
                        onLoad={handleImageLoad}
                        onError={handleImageError}
                        style={{ display: imageLoaded ? "block" : "none" }}
                      />
                    </div>
                  ) : (
                    <img
                      src={dataGetProductById?.product?.imageURL}
                      alt="Current product image"
                      className={ProductListStyles.imagePreview}
                    />
                  )}
                  <p id="update-merch-dropzone-instructions" className={ProductListStyles.visuallyHidden}>
                    Click or drag to upload a new image. Accepts JPEG and PNG.
                  </p>
                </div>
                <h2>Basic Details</h2>
                <div className={ProductListStyles.dropDownFlexContent}>
                  <label id="update-merch-category-label">Category</label>
                  <CustomSelectProduct
                    options={categoryOptions}
                    onSelect={handleSelectTypeAndCategory("category")}
                    placeholder="Select category"
                    value={
                      categoryOptions.find(
                        (option) => option.value === createProductForm?.category
                      ) || null
                    }
                    id="update-merch-category-select"
                  />
                </div>
                <Input
                  type="text"
                  label="Product Name"
                  onChangeHandler={inputChangeHandler}
                  name="productName"
                  value={createProductForm?.productName}
                />
                <div className={ProductListStyles.inputFlexBox}>
                  <div>
                    <Input
                      type="number"
                      label="Price"
                      onChangeHandler={inputChangeHandler}
                      name="price"
                      maxLength={6}
                      value={
                        createProductForm?.price
                          ? createProductForm.price.toString()
                          : ""
                      }
                    />
                  </div>
                  <div>
                    <Input
                      type="number"
                      label="Quantity"
                      onChangeHandler={inputChangeHandler}
                      name="quantity"
                      maxLength={6}
                      value={
                        createProductForm?.quantity !== null && createProductForm?.quantity !== undefined
                          ? createProductForm.quantity.toString()
                          : ""
                      }
                    />
                  </div>
                </div>
                <div className={ProductListStyles.addOnSection}>
                  <div className={ProductListStyles.flexBoxBlk}>
                    <div>
                      <h1 id="update-addons-toggle-label">Add ons</h1>
                    </div>
                    <div>
                      <ToggleSwitch
                        toggleState={toggleState}
                        setToggleState={setToggleState}
                        addOns={createProductForm?.addOns}
                        disable={
                          createProductForm?.addOns.length > 0 ? true : false
                        }
                        label="Enable add-ons"
                        id="update-addons-toggle"
                      />
                    </div>
                  </div>

                  {toggleState && (
                    <CustomSelectAddOns
                      options={filterDataAddOns}
                      onSelect={handleSelectAddOns}
                      setCreateProductForm={setCreateProductForm}
                      placeholder="Select Add-Ons"
                      id="update-addons-select"
                    />
                  )}

                  {createProductForm?.addOns.length === 0 ? null : (
                    <div 
                      className={ProductListStyles.addOnFlex}
                      role="list"
                      aria-label="Selected add-ons"
                    >
                      {createProductForm?.addOns.map(
                        (data: AddOnProps, index: number) => {
                          return (
                            <div 
                              key={index} 
                              className={ProductListStyles.addOn}
                              role="listitem"
                            >
                              <span>{data?.addonName}</span>
                              <button
                                type="button"
                                className={ProductListStyles.addOnRemoveButton}
                                onClick={() =>
                                  removeListAddOns(Number(data?.addonId))
                                }
                                aria-label={`Remove ${data?.addonName} add-on`}
                              >
                                ×
                              </button>
                            </div>
                          );
                        }
                      )}
                    </div>
                  )}
                </div>
                <Input
                  type="text"
                  label="Description"
                  onChangeHandler={inputChangeHandler}
                  name="productDescription"
                  value={createProductForm?.productDescription || ""}
                />
                <hr
                  className={ProductListStyles.horizontalLine}
                  style={{ marginTop: "30px", marginBottom: "20px" }}
                />
                <MerchVariant
                  createProductForm={createProductForm}
                  setCreateProductForm={setCreateProductForm}
                />
                <p className={ProductListStyles.variantQuantityNote}>
                  Variants are optional. If you add size variants, stock will be managed per size. Otherwise, the Quantity field above will be used.
                </p>
              </div>
            )}

            <hr
              className={ProductListStyles.horizontalLine}
              style={{ marginTop: "30px", marginBottom: "20px" }}
            />
            {!errorHandler.success && errorHandler?.message ? (
              <Message variant="error">{errorHandler.message}</Message>
            ) : null}

            <button
              className={ProductListStyles.buttonCreateProduct}
              disabled={createProductIsLoading}
              onClick={updateProductionSubmitHandler}
            >
              {createProductIsLoading ? (
                <Spinner variant="small-spinner" alignSpin="center-spinner" />
              ) : (
                <>
                  Update Product<i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UpdateProductModal;
